import React from "react";
import { twMerge } from "tailwind-merge";
import { AlertCircle, CheckCircle2 } from "lucide-react";

const InputField = React.forwardRef(
  ({ type = "text", label, error, success, helpText, className, id, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const baseStyles = `
    w-full px-4 py-3 rounded-xl border outline-none transition-[box-shadow,border-color] duration-150
    placeholder:text-neutral-400 focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-offset-1
    disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50
  `;

    const variants = {
      default: `
      border-neutral-200 bg-white text-neutral-900
      focus:border-primary focus:ring-primary/20
    `,
      error: `
      border-error bg-error/5 text-error-900
      focus:border-error focus:ring-error/20
    `,
      success: `
      border-success bg-success/5 text-success-900
      focus:border-success focus:ring-success/20
    `,
    };

    const getVariant = () => {
      if (error) return "error";
      if (success) return "success";
      return "default";
    };

    const inputClasses = twMerge(baseStyles, variants[getVariant()], className);

    return (
      <div className="space-y-2">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-neutral-700">
            {label}
          </label>
        )}

        <div className="relative">
          <input ref={ref} type={type} id={inputId} className={inputClasses} {...props} />

          {error && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <AlertCircle className="h-5 w-5 text-error" />
            </div>
          )}

          {success && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <CheckCircle2 className="h-5 w-5 text-success" />
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-error flex items-center gap-1">
            <AlertCircle className="h-4 w-4" />
            {error}
          </p>
        )}

        {success && (
          <p className="text-sm text-success flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4" />
            {success}
          </p>
        )}

        {helpText && !error && !success && <p className="text-sm text-neutral-500">{helpText}</p>}
      </div>
    );
  }
);

InputField.displayName = "InputField";

export default InputField;
